import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';

const columns = [
  { key: 'rank', label: 'Rank', type: 'number', sortable: true },
  { key: 'username', label: 'Username', type: 'text', sortable: true },
  { key: 'player_id', label: 'Player ID', type: 'text', sortable: true },
  { key: 'composite_score', label: 'Score', type: 'number', sortable: true },
  { key: 'win_percentage', label: 'Win %', type: 'number', sortable: true },
  { key: 'total_battles', label: 'Battles', type: 'number', sortable: true },
  { key: 'total_kills', label: 'Kills', type: 'number', sortable: true },
  { key: 'kills_per_battle', label: 'K/B', type: 'number', sortable: true },
  { key: 'highest_damage', label: 'Max Dmg', type: 'number', sortable: true },
  { key: 'inventory_power', label: 'Power', type: 'number', sortable: true },
  { key: 'updated_at', label: 'Updated', type: 'text', sortable: true },
];

const AdminPlayersPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { get, post } = useAdminApi();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [purgeDays, setPurgeDays] = useState(90);
  const [actionResult, setActionResult] = useState('');

  const loadData = useCallback(async () => {
    try {
      const data = await get('/get-all-players.php');
      setPlayers(data?.players || []);
    } catch { /* empty */ } finally { setLoading(false); }
  }, [get]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await post('/delete-player.php', { player_id: deleteTarget.row.player_id });
      setDeleteTarget(null);
      await loadData();
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleBulkAction = async (action) => {
    try {
      setActionResult('');
      const body = { action };
      if (action === 'purge_stale') body.days = purgeDays;

      if (action === 'export') {
        const data = await post('/admin-bulk-players.php', body);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'players-export.json';
        a.click();
        URL.revokeObjectURL(url);
        setActionResult('Export downloaded');
        return;
      }

      const res = await post('/admin-bulk-players.php', body);
      setActionResult(res.message || 'Done');
      setBulkAction(null);
      await loadData();
    } catch (err) { alert('Action failed: ' + err.message); }
  };

  // Flag suspicious players
  const flaggedPlayers = players.map(p => ({
    ...p,
    _suspicious: p.win_percentage > 95 || p.kills_per_battle > 30,
  }));

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>
          Players ({players.length})
        </h1>
        <div className="flex gap-2">
          <button onClick={() => handleBulkAction('export')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Export
          </button>
          <button onClick={() => setBulkAction('purge_stale')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Purge Stale
          </button>
          <button onClick={() => handleBulkAction('recalculate_ranks')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Recalculate Ranks
          </button>
          <button onClick={() => handleBulkAction('cleanup_duplicates')}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Cleanup Dupes
          </button>
        </div>
      </div>

      {actionResult && (
        <div className={`text-sm px-4 py-2 rounded-lg ${dark ? 'bg-green-900/20 text-green-400 border border-green-800' : 'bg-green-50 text-green-700 border border-green-200'}`}>
          {actionResult}
        </div>
      )}

      <DataTable
        columns={columns}
        data={flaggedPlayers}
        onDelete={(row, index) => setDeleteTarget({ row, index })}
      />

      {/* Purge stale dialog */}
      {bulkAction === 'purge_stale' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setBulkAction(null)} />
          <div className={`relative w-full max-w-sm p-6 rounded-xl shadow-xl ${dark ? 'bg-[#1A1A1A] border border-[#333]' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${dark ? 'text-white' : 'text-gray-900'}`}>Purge Stale Players</h3>
            <p className={`text-sm mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              Remove players not updated in more than:
            </p>
            <div className="flex items-center gap-2 mb-6">
              <input
                type="number"
                value={purgeDays}
                onChange={(e) => setPurgeDays(Number(e.target.value))}
                min={1}
                className={`w-24 px-3 py-2 rounded-lg border text-sm ${dark ? 'bg-[#121212] border-[#333] text-white' : 'bg-white border-gray-300'}`}
              />
              <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>days</span>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setBulkAction(null)}
                className={`px-4 py-2 rounded-lg text-sm ${dark ? 'text-gray-400 hover:bg-[#333]' : 'text-gray-600 hover:bg-gray-100'}`}>
                Cancel
              </button>
              <button onClick={() => handleBulkAction('purge_stale')}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                Purge
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Player"
        message={`Delete "${deleteTarget?.row?.username}" (${deleteTarget?.row?.player_id})?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AdminPlayersPage;
